import React from 'react'
import {
  MapContainer,
  TileLayer,
  ImageOverlay,
} from 'react-leaflet'

import 'leaflet.sync'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'

import { makeStyles } from '@material-ui/core/styles'
import { computeIndex } from '../../../../utils/gradient'

import CloudLegend from './CloudLegend'
import Grid from '@material-ui/core/Grid'
import CircularProgress from '@material-ui/core/CircularProgress'
import { getParcelBounds } from '../../../../utils/map'
import { createImageUrlWithDate } from '../../../../utils/parcels'
import { createCanvasForParcel } from '../../../../utils/map'
import { useSatelliteImages } from '../../../../context/SatelliteImagesContext'
import { convertArrayToObject } from '../../../../utils'
import { useAuth } from '../../../../context/AuthContext'
import MapLayersManager from '../../MapLayersManager'
import LayerLegend from './LayerLegend'
import { usePaletteScript, withPaletteScript } from '../../../../adapters/paletteRange'
import { usePaletteAdjustmentsEnabled } from '../../../../context/PaletteAdjustmentsContext'
import { rescaleLegend } from '../../../../utils/palette'

// Center map on Granada
const position = [37.741934, -3.188515]

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  loading: {
    marginBottom: theme.spacing(20),
    marginTop: theme.spacing(20),
  },
}))

function LittleMap({
  parcel,
  scrData,
  map,
  setMap,
  layer,
  selectedResolution,
  baseRef,
  comparedRef,
  width,
  height = 400,
}) {
  const classes = useStyles()
  const [canvas, setCanvas] = React.useState(null)
  const [canvasLoading, setCanvasLoading] = React.useState(true)
  const [colorPointer, setColorPointer] = React.useState(null)

  const { loggedUser } = useAuth()
  const { satelliteImages, dispatchSatelliteImages } = useSatelliteImages()

  // The user's saved palette adjustment for this farm/layer, so the comparator renders
  // the same colours as the main map (only in apps that enable palette adjustments)
  const paletteEnabled = usePaletteAdjustmentsEnabled()
  const { data: palette, isLoading: paletteLoading } = usePaletteScript(
    { parcelId: parcel?.id, layerKey: layer?.key, date: scrData?.date },
    { enabled: paletteEnabled }
  )
  const paletteUrl = palette?.evalscript_url || null
  const legendLayer = React.useMemo(
    () =>
      paletteUrl
        ? { ...layer, legend: rescaleLegend(layer.legend, palette) }
        : layer,
    [layer, paletteUrl, palette?.min, palette?.max]
  )

  const index = React.useMemo(
    () =>
      colorPointer
        ? computeIndex(colorPointer, legendLayer.legend, scrData?.basicStats)
        : null,
    [colorPointer, legendLayer]
  )

  React.useEffect(() => {
    if (canvas) {
      if (!(canvas.imageUrl in satelliteImages))
        dispatchSatelliteImages({
          type: 'ADD_IMAGES',
          payload: convertArrayToObject([canvas], 'parcelUrl'),
        })
    }
  }, [canvas])

  React.useEffect(() => {
    if (canvas) {
      setCanvas(null)
      setCanvasLoading(true)
    }
  }, [parcel, scrData, paletteUrl])

  React.useEffect(() => {
    if (scrData?.date && !paletteLoading) {
      const imageUrl = withPaletteScript(
        createImageUrlWithDate(parcel, layer, scrData.date, selectedResolution),
        palette
      )

      if (`${imageUrl},${parcel.id}` in satelliteImages) {
        setCanvasLoading(false)
        setCanvas(satelliteImages[`${imageUrl},${parcel.id}`])
      } else {
        const parcelCanvas = createCanvasForParcel(
          parcel,
          imageUrl,
          selectedResolution,
          layer,
          () => {
            setCanvasLoading(false)
          }
        )
        setCanvas(parcelCanvas)
      }
    }
  }, [layer, scrData, paletteLoading, paletteUrl])

  React.useEffect(() => {
    if (map) {
      try {
        map.fitBounds(getParcelBounds(parcel))
      } catch (error) {
        console.error(error)
      }
    }
  }, [map, scrData, parcel])

  function transform(anchor_width, anchor_height, x, y) {
    return [
      (x / anchor_width) * canvas.canvas.width,
      (y / anchor_height) * canvas.canvas.height,
    ]
  }

  return (
    <>
      {canvas && !canvasLoading && scrData?.date ? (
        <MapContainer
          center={position}
          minZoom={2}
          maxZoom={24}
          zoom={9}
          style={{ width: '100%', height, minHeight: 360, borderRadius: 4 }}
          zoomControl={false}
          whenCreated={setMap}
        >
          {!isWidthUp('sm', width) && (
            <TileLayer
              maxZoom={24}
              maxNativeZoom={20}
              attribution="Maps Hybrid"
              url={`https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}`}
            />
          )}
          <MapLayersManager
            companyId={loggedUser?.company?.id}
            map={map}
            width={width}
            openAddParcel={{ open: false }}
            noAppBar
            controlTop={12}
            controlRight={12}
            forceSimpleControl
            hideOverlays
          />
          <ImageOverlay
            key={canvas?.parcelUrl}
            bounds={getParcelBounds(parcel)}
            url={canvas.canvas.toDataURL()}
            transparent={true}
            ref={baseRef}
            eventHandlers={{
              mousemove: (e) => {
                if (layer.legend?.length > 0 && !palette?.shade) {
                  const projCoords = transform(
                    e.target._image.clientWidth,
                    e.target._image.clientHeight,
                    e.originalEvent.offsetX,
                    e.originalEvent.offsetY
                  )

                  setColorPointer(
                    canvas.context.getImageData(
                      projCoords[0],
                      projCoords[1],
                      1,
                      1
                    ).data
                  )

                  if (!e.initiator && comparedRef.current)
                    comparedRef.current.fire('mousemove', {
                      latlng: e.latlng,
                      originalEvent: e.originalEvent,
                      initiator: true,
                    })

                  baseRef.current
                    .bindTooltip(
                      `Value: <b style="background: ${index?.color}; color: 'black'; font-size:16px">${index?.value}</b><br />`
                    )
                    .openTooltip(e.latlng)
                }
              },
              mouseout: (e) => {
                if (!e.initiator && comparedRef.current)
                  comparedRef.current.fire('mouseout', {
                    initiator: true,
                  })

                baseRef.current.unbindTooltip()
              },
            }}
            interactive
          ></ImageOverlay>
          <CloudLegend cloud_coverage={scrData.cloud_coverage} />
          <LayerLegend layer={legendLayer} customRange={!!paletteUrl} />
        </MapContainer>
      ) : (
        <Grid item xs={3} align="center">
          <CircularProgress className={classes.loading} />
        </Grid>
      )}
    </>
  )
}

export default withWidth()(LittleMap)
